import React, { useState, useEffect, useRef, useMemo } from 'react';
import LocalizedLink from './LocalizedLink';
import useLocale from '../hooks/useLocale';
import useCommonContent from '../hooks/useCommonContent';
import {
  Facebook, Instagram, Linkedin, Youtube, Twitter, Github,
  Mail, Phone, MapPin, ArrowRight, Send, CheckCircle, Loader2
} from 'lucide-react';
import API from '../constants/api';
import { isTurnstileEnabled } from '../utils/turnstile';

const TURNSTILE_SITE_KEY = '0x4AAAAAAClTXZq-YF03VR_0';

const Footer = () => {
  const locale = useLocale();
  const { labels } = useCommonContent(locale);
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileEnabled = isTurnstileEnabled();

  // Render Turnstile widget (script loaded globally in index.html)
  useEffect(() => {
    if (!turnstileEnabled) {
      return undefined;
    }

    let widgetId = null;
    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current) {
        widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'dark',
          size: 'compact',
          callback: (token) => setTurnstileToken(token),
        });
        return true;
      }
      return false;
    };
    if (!renderWidget()) {
      const poll = setInterval(() => { if (renderWidget()) clearInterval(poll); }, 500);
      return () => clearInterval(poll);
    }
    return () => { if (widgetId != null && window.turnstile) window.turnstile.remove(widgetId); };
  }, [turnstileEnabled]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      const res = await fetch(API.subscribe, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, turnstile_token: turnstileToken }),
      });
      const data = await res.json();
      if (data.success) {
        setIsSubscribed(true);
        setEmail('');
        setTimeout(() => setIsSubscribed(false), 5000);
      } else {
        setErrorMsg(data.error || labels.errorRetry);
      }
    } catch {
      setErrorMsg(labels.connectionError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com/people/Tiguidou-Creation-web/61576478671852/', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/quebecstore_tiguidou/', label: 'Instagram' },
    { icon: Twitter, href: 'https://twitter.com/Eren1QC', label: 'Twitter' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/Tiguidou-web-Quebecstore-GasoRene', label: 'LinkedIn' },
    { icon: Github, href: 'https://github.com/Re-PeaceFront', label: 'GitHub' },
    { icon: Youtube, href: 'https://www.youtube.com/@QuebecDefender', label: 'YouTube' },
    {
      icon: () => (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
        </svg>
      ),
      href: 'https://www.tiktok.com/@tiguidou_creation_web',
      label: 'TikTok'
    },
  ];

  const footerLinks = useMemo(() => ({
    company: [
      { label: labels.about, href: '/a-propos-agence-web-sherbrooke' },
      { label: labels.blog, href: '/blogue-technologie-web' },
      { label: 'Portfolio', href: '/portfolio-realisations-web' },
    ],
    services: [
      { label: labels.webDesignFooter, href: '/services/conception-site-web-sherbrooke' },
      { label: 'E-Commerce', href: '/services/boutique-en-ligne-ecommerce' },
      { label: labels.googleAds, href: '/services/publicite-google-ads-facebook' },
      { label: `${labels.aica} (IA)`, href: '/aica' },
    ],
    support: [
      { label: labels.contactUs, href: '/contact-agence-web-sherbrooke' },
      { label: labels.freeQuote, href: '/soumission-projet-web-gratuit' },
      { label: 'FAQ', href: '/faq' },
      { label: labels.sitemap, href: '/plan-du-site' },
    ],
    legal: [
      { label: labels.privacy, href: '/politique-confidentialite' },
      { label: labels.terms, href: '/conditions-utilisation' },
      { label: labels.serviceContract, href: '/contrat-service-general.pdf', external: true },
    ],
  }), [labels]);

  return (
    <footer className="bg-slate-900 text-white">
      {/* CTA Section */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 p-8 rounded-2xl bg-blue-600/10 border border-blue-500/20">
            <div className="text-center lg:text-left">
              <h3 className="text-xl lg:text-2xl font-bold mb-1">
                {labels.footerCtaTitle}
              </h3>
              <p className="text-slate-400">
                {labels.footerCtaSub}
              </p>
            </div>
            <LocalizedLink
              to="/soumission-projet-web-gratuit"
              className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              {labels.freeQuote}
              <ArrowRight className="w-4 h-4" />
            </LocalizedLink>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">

          {/* Brand Column */}
          <div className="col-span-2 space-y-4">
            <LocalizedLink to="/">
              <img
                src={require('../Assets/TIGUIDOU_CREATION WEB-Logo-Oficial-Quebecstore_Inc-www_quebecstore_ca.png')}
                alt="Tiguidou Création Web — quebecstore.ca"
                className="h-10 w-auto brightness-0 invert"
              />
            </LocalizedLink>
            <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
              {labels.footerTagline}
            </p>

            {/* Contact */}
            <div className="space-y-2 text-sm">
              <a href="tel:819-452-4163" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-blue-500" />
                819-452-4163
              </a>
              <a href="mailto:sales@quebecstore.ca" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-blue-500" />
                sales@quebecstore.ca
              </a>
              <div className="flex items-center gap-2 text-slate-500">
                <MapPin className="w-4 h-4" />
                Sherbrooke, QC
              </div>
            </div>

            {/* Social */}
            <div className="flex items-center gap-2 pt-2">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">{labels.company}</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <LocalizedLink to={link.href} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">{labels.services}</h4>
            <ul className="space-y-2">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <LocalizedLink to={link.href} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">{labels.support}</h4>
            <ul className="space-y-2">
              {footerLinks.support.map((link, index) => (
                <li key={index}>
                  <LocalizedLink to={link.href} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">{labels.newsletter}</h4>
            <form onSubmit={handleNewsletterSubmit} className="space-y-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={labels.emailPlaceholder}
                required
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
              />
              {turnstileEnabled && <div ref={turnstileRef} className="mb-1"></div>}
              <button
                type="submit"
                disabled={isSubscribed || isSubmitting}
                className={`w-full py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${isSubscribed ? 'bg-green-600' : isSubmitting ? 'bg-slate-600' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {isSubscribed ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {labels.subscribed}
                  </>
                ) : isSubmitting ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {labels.sending}
                  </>
                ) : (
                  <>
                    <Send className="w-3 h-3" />
                    {labels.subscribe}
                  </>
                )}
              </button>
              {errorMsg && <p className="text-xs text-red-400">{errorMsg}</p>}
            </form>
            <LocalizedLink to="/newsletter/unsubscribe" className="block mt-2 text-xs text-slate-600 hover:text-slate-400 transition-colors">
              {labels.unsubscribe}
            </LocalizedLink>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <span>© {new Date().getFullYear()} Tiguidou Création Web</span>
              <span>•</span>
              <span>NEQ: 2280917511</span>
            </div>

            <div className="flex items-center gap-4">
              {footerLinks.legal.map((link, index) =>
                link.external ? (
                  <a key={index} href={link.href} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    {link.label}
                  </a>
                ) : (
                  <LocalizedLink key={index} to={link.href} className="hover:text-white transition-colors">
                    {link.label}
                  </LocalizedLink>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
